/**
 * Per-scene registry of live MSDF text sources, for RTT capture passes.
 *
 * MSDF glyphs are not scene meshes — the Text behavior draws them
 * imperatively into the currently bound framebuffer during the live
 * main-camera render (`scene.onAfterCameraRenderObservable`). An RTT render
 * (`RenderTargetTexture.render()`) never fires that observable, so any
 * RTT-based capture (thumbnails, publish previews) must invoke the draw
 * itself — but the capture code holds a Babylon `Scene`, not a `TraceScene`,
 * and cannot enumerate `Text` behaviors. This registry is the bridge: the
 * `Text` behavior registers itself while its renderer is live, and capture
 * passes (`editor/ThumbnailCapture.ts`) query per scene.
 *
 * Lifecycle contract: register after the renderer is created, unregister
 * when it is disposed (which the Text behavior does on detach and at the
 * start of every font reload) — the registry therefore never hands a
 * capture a disposed renderer. Entries expose live queries only (no cached
 * nodes), so reparenting (e.g. `onAnimationRootCreated`) can't go stale.
 *
 * The registry is keyed by a WeakMap, so a disposed scene releases its set
 * even if a behavior missed its unregister.
 */

import type { Scene } from '@babylonjs/core/scene';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Matrix } from '@babylonjs/core/Maths/math.vector';

/**
 * What a capture pass needs from a live text source. Implemented by the
 * `Text` behavior; kept structural so this module depends on no behavior
 * classes (and so tests can register lightweight fakes).
 */
export interface TextCaptureSource {
    /**
     * The node the glyphs are parented to (queried LIVE — reparenting-safe).
     * Null before attach. Subtree captures use this to scope the pass.
     */
    getTextRootNode(): TransformNode | null;

    /**
     * True when a capture can draw this text right now: the MSDF shader
     * effect is compiled, or there is nothing to draw. Readiness waits poll
     * this before a single-pass render — `TextRenderer.render()` silently
     * early-returns while its effect is still compiling, so rendering
     * without the wait can drop the text from a one-shot capture.
     */
    isCaptureReady(): boolean;

    /**
     * Draw the glyphs with the given view/projection, applying the same
     * guards as the live per-camera draw (glyphs present, subtree enabled).
     */
    renderGlyphs(view: Matrix, projection: Matrix): void;
}

const REGISTRY = new WeakMap<Scene, Set<TextCaptureSource>>();

/**
 * Shared empty result for scenes with no text. The publish preview queries
 * the registry once per rendered frame; returning a constant keeps the
 * text-free steady state allocation-free.
 */
const NO_SOURCES: readonly TextCaptureSource[] = [];

/** Register a live text source for `scene`. Idempotent per source. */
export function registerTextCaptureSource(scene: Scene, source: TextCaptureSource): void {
    let set = REGISTRY.get(scene);
    if (!set) {
        set = new Set();
        REGISTRY.set(scene, set);
    }
    set.add(source);
}

/** Remove a text source for `scene`. No-op when absent. */
export function unregisterTextCaptureSource(scene: Scene, source: TextCaptureSource): void {
    REGISTRY.get(scene)?.delete(source);
}

/** Snapshot of the scene's live text sources (empty for text-free scenes). */
export function textCaptureSourcesForScene(scene: Scene): readonly TextCaptureSource[] {
    const set = REGISTRY.get(scene);
    return set && set.size > 0 ? Array.from(set) : NO_SOURCES;
}
